import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

interface FAQ {
  id: number;
  category: string;
  question: string;
  answer: string;
  tags: string[];
}

const FAQPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('toate');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqs: FAQ[] = [
    // Cont și Autentificare
    {
      id: 1,
      category: 'cont',
      question: 'Cum îmi creez un cont nou?',
      answer: 'Pentru a crea un cont nou, apasă pe butonul "Înregistrare" din partea de sus a paginii. Completează formularul cu numele, adresa de email și parola dorită. Vei primi un email de confirmare pentru a activa contul.',
      tags: ['cont', 'înregistrare', 'email', 'parola']
    },
    {
      id: 2,
      category: 'cont',
      question: 'Am uitat parola. Cum o pot reseta?',
      answer: 'Pe pagina de autentificare, apasă pe linkul "Am uitat parola". Introdu adresa de email asociată contului tău și vei primi instrucțiuni pentru resetarea parolei. Verifică și folderul spam/junk.',
      tags: ['parola', 'resetare', 'email', 'uitată']
    },
    {
      id: 3,
      category: 'cont',
      question: 'Pot să îmi schimb adresa de email?',
      answer: 'Da, poți schimba adresa de email din secțiunea "Setări cont" din profilul tău. Va fi necesară confirmarea noii adrese prin email.',
      tags: ['email', 'schimbare', 'setări', 'profil']
    },
    
    // Utilizare
    {
      id: 4,
      category: 'utilizare',
      question: 'Cum pot căuta cărți în bibliotecă?',
      answer: 'Poți folosi bara de căutare de pe pagina principală sau din meniul de navigare. Poți căuta după titlu, autor, gen sau cuvinte cheie. Pentru căutări mai precise, folosește opțiunea "Căutare avansată".',
      tags: ['căutare', 'cărți', 'titlu', 'autor', 'avansată']
    },
    {
      id: 5,
      category: 'utilizare',
      question: 'Cum pot descărca o carte pentru citire offline?',
      answer: 'Deschide cartea dorită și apasă pe butonul "Descarcă" din interfața de citire. Cărțile pot fi descărcate în format PDF sau EPUB, în funcție de disponibilitate. Ai nevoie de un cont pentru această funcționalitate.',
      tags: ['descărcare', 'offline', 'PDF', 'EPUB', 'cont']
    },
    {
      id: 6,
      category: 'utilizare',
      question: 'Cum fac evidențieri și note în cărți?',
      answer: 'În interfața de citire, selectează textul pe care vrei să îl evidențiezi. Vei vedea un meniu cu opțiuni pentru evidențiere, notițe și marcaje. Toate adnotările tale sunt salvate automat în contul tău.',
      tags: ['evidențieri', 'note', 'adnotări', 'selectare', 'salvare']
    },
    {
      id: 7,
      category: 'utilizare',
      question: 'Pot citi cărțile pe telefon sau tabletă?',
      answer: 'Da, platforma este complet responsivă și funcționează pe toate dispozitivele: telefoane, tablete și computere. Progresul de lectură se sincronizează automat între dispozitive.',
      tags: ['mobil', 'tabletă', 'responsive', 'sincronizare', 'progres']
    },

    // Acces și Permisiuni
    {
      id: 8,
      category: 'acces',
      question: 'Biblioteca este gratuită?',
      answer: 'Da, majoritatea cărților și resurselor din biblioteca noastră sunt disponibile gratuit. Unele colecții speciale sau premium pot necesita un abonament, dar o gamă largă de conținut este accesibil fără cost.',
      tags: ['gratuit', 'cost', 'abonament', 'premium', 'acces']
    },
    {
      id: 9,
      category: 'acces',
      question: 'De ce unele cărți nu sunt disponibile în țara mea?',
      answer: 'Disponibilitatea cărților poate fi restricționată din motive de drepturi de autor și licențe geografice. Lucrăm constant pentru a extinde accesul global la cât mai multe resurse.',
      tags: ['restricții', 'geografic', 'copyright', 'licențe', 'țară']
    },
    {
      id: 10,
      category: 'acces',
      question: 'Pot partaja cărțile cu alți utilizatori?',
      answer: 'Poți partaja link-uri către cărțile disponibile public din bibliotecă. Nu este permisă redistribuirea fișierelor descărcate din motive de protecție a drepturilor de autor.',
      tags: ['partajare', 'link', 'redistribuire', 'copyright', 'fișiere']
    },

    // Probleme Tehnice
    {
      id: 11,
      category: 'tehnic',
      question: 'Cărțile se încarcă foarte lent. Ce pot face?',
      answer: 'Verifică conexiunea la internet și închide alte aplicații care consumă bandă. Încearcă să reîmprospătezi pagina sau să te deconectezi și conectezi din nou. Dacă problema persistă, contactează suportul.',
      tags: ['lent', 'încărcare', 'internet', 'conexiune', 'reîmprospătare']
    },
    {
      id: 12,
      category: 'tehnic',
      question: 'Nu pot deschide fișierele PDF descărcate. De ce?',
      answer: 'Asigură-te că ai un cititor PDF instalat (Adobe Reader, Foxit, etc.). Verifică dacă descărcarea s-a finalizat complet. Unele fișiere pot fi protejate cu parolă - contactează suportul pentru asistență.',
      tags: ['PDF', 'deschidere', 'cititor', 'descărcare', 'parolă']
    },
    {
      id: 13,
      category: 'tehnic',
      question: 'Site-ul nu funcționează pe browser-ul meu. Ce fac?',
      answer: 'Recomandăm folosirea unei versiuni recente de Chrome, Firefox, Safari sau Edge. Curăță cache-ul și cookie-urile browser-ului sau încearcă în modul incognito/privat.',
      tags: ['browser', 'compatibilitate', 'cache', 'cookies', 'incognito']
    },

    // Drepturi de Autor
    {
      id: 14,
      category: 'copyright',
      question: 'Pot folosi cărțile din bibliotecă în proiectele mele?',
      answer: 'Depinde de licența fiecărei cărți. Cărțile din domeniul public pot fi folosite liber. Pentru alte materiale, verifică licența specifică sau contactează-ne pentru clarificări.',
      tags: ['utilizare', 'proiecte', 'licență', 'domeniu public', 'permisiuni']
    },
    {
      id: 15,
      category: 'copyright',
      question: 'Cum pot sugera o carte pentru a fi adăugată?',
      answer: 'Folosește formularul de contact sau secțiunea "Sugerează o carte" din contul tău. Menționează detaliile cărții și motivul pentru care ar fi valoroasă pentru comunitate. Evaluăm toate sugestiile primite.',
      tags: ['sugestii', 'adăugare', 'cărți', 'formular', 'contact']
    }
  ];

  const categories = [
    { id: 'toate', name: 'Toate categoriile', icon: '📚' },
    { id: 'cont', name: 'Cont și Autentificare', icon: '👤' },
    { id: 'utilizare', name: 'Utilizare și Funcționalități', icon: '⚙️' },
    { id: 'acces', name: 'Acces și Permisiuni', icon: '🔐' },
    { id: 'tehnic', name: 'Probleme Tehnice', icon: '🔧' },
    { id: 'copyright', name: 'Drepturi de Autor', icon: '⚖️' }
  ];

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = selectedCategory === 'toate' || faq.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return matchesCategory && matchesSearch;
  });

  const toggleFaq = (id: number) => {
    setOpenFaq(openFaq === id ? null : id);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Header />
      
      {/* Spațiu pentru header-ul fix */}
      <div className="pt-24 md:pt-28"></div>
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="text-center">
              <div className="text-6xl mb-6">❓</div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Întrebări Frecvente</h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8">
                Găsește răspunsuri rapide la cele mai comune întrebări despre biblioteca digitală
              </p>
              
              {/* Search bar */}
              <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-2">
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-400 ml-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Caută în întrebările frecvente..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 px-4 py-3 text-gray-700 bg-transparent outline-none"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="p-2 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick navigation */}
        <section className="py-8 bg-white border-b">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-6 py-3 rounded-full font-medium transition-all ${
                    selectedCategory === category.id
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="mr-2">{category.icon}</span>
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Results count and FAQ list */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              {/* Results info */}
              <div className="mb-8 text-center">
                <p className="text-gray-600">
                  {filteredFaqs.length > 0 ? (
                    <>
                      Găsite <span className="font-bold text-blue-600">{filteredFaqs.length}</span> întrebări
                      {searchQuery && (
                        <> pentru "<span className="font-medium">{searchQuery}</span>"</>
                      )}
                      {selectedCategory !== 'toate' && (
                        <> în categoria <span className="font-medium">{categories.find(c => c.id === selectedCategory)?.name}</span></>
                      )}
                    </>
                  ) : (
                    'Nu au fost găsite rezultate pentru căutarea ta'
                  )}
                </p>
              </div>

              {/* FAQ List */}
              <div className="space-y-4">
                {filteredFaqs.map(faq => (
                  <div key={faq.id} className="bg-white rounded-xl shadow-md overflow-hidden">
                    <button
                      onClick={() => toggleFaq(faq.id)}
                      className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition"
                    >
                      <div className="flex items-start">
                        <span className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold mr-4 mt-1">
                          {categories.find(c => c.id === faq.category)?.icon}
                        </span>
                        <h3 className="text-lg font-medium text-gray-800 pr-4">{faq.question}</h3>
                      </div>
                      <svg
                        className={`w-6 h-6 text-gray-400 transform transition-transform flex-shrink-0 ${
                          openFaq === faq.id ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {openFaq === faq.id && (
                      <div className="px-6 pb-6">
                        <div className="pl-12">
                          <p className="text-gray-600 leading-relaxed mb-4">{faq.answer}</p>
                          <div className="flex flex-wrap gap-2">
                            {faq.tags.map(tag => (
                              <span
                                key={tag}
                                className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full cursor-pointer hover:bg-blue-100 hover:text-blue-700 transition"
                                onClick={() => setSearchQuery(tag)}
                              >
                                #{tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* No results */}
              {filteredFaqs.length === 0 && (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🔍</div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Nu am găsit răspunsuri</h3>
                  <p className="text-gray-600 mb-6">
                    Încearcă să reformulezi căutarea sau să alegi o altă categorie
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedCategory('toate');
                      }}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    >
                      Vezi toate întrebările
                    </button>
                    <Link
                      to="/contact"
                      className="px-6 py-3 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition"
                    >
                      Contactează suportul
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Contact section */}
        <section className="py-16 bg-blue-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">Nu ai găsit răspunsul căutat?</h2>
              <p className="text-gray-600 mb-8 text-lg">
                Echipa noastră de suport este aici să te ajute cu orice întrebare sau problemă
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Link 
                  to="/contact" 
                  className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition group"
                >
                  <div className="text-blue-600 text-3xl mb-4 group-hover:scale-110 transition-transform">💬</div>
                  <h3 className="font-bold text-gray-800 mb-2">Contactează-ne</h3>
                  <p className="text-gray-600 text-sm">Trimite-ne un mesaj direct</p>
                </Link>
                
                <Link 
                  to="/help" 
                  className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition group"
                >
                  <div className="text-blue-600 text-3xl mb-4 group-hover:scale-110 transition-transform">📖</div>
                  <h3 className="font-bold text-gray-800 mb-2">Ghiduri detaliate</h3>
                  <p className="text-gray-600 text-sm">Documentație completă</p>
                </Link>
                
                <a 
                  href="mailto:contact@lexora.docdill.ro" 
                  className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition group"
                >
                  <div className="text-blue-600 text-3xl mb-4 group-hover:scale-110 transition-transform">📧</div>
                  <h3 className="font-bold text-gray-800 mb-2">Email direct</h3>
                  <p className="text-gray-600 text-sm">contact@lexora.docdill.ro</p>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default FAQPage; 
